import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '../components/DashboardLayout';
import { getQuestionnaireById, submitQuestionnaire, submitStartSurvey } from '../services/questionnaires/questionnaire.service';
import { Questionnaire } from '../types/questionnaire';
import { QuestionnaireRenderer } from '../services/questionnaires/questionnaireRenderer';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Snackbar,
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';

const AlertSnackbar = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const QuestionnairePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isStartSurvey, setIsStartSurvey] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchQuestionnaire = async () => {
      try {
        setLoading(true);

        // Перевіряємо, чи це стартове опитування, дивлячись на URL параметр
        // або на сам id, якщо перехід був прямим
        const searchParams = new URLSearchParams(location.search);
        const isFromStartFlow = searchParams.get('from') === 'start';
        setIsStartSurvey(id === 'start-survey' || isFromStartFlow);

        const data = await getQuestionnaireById(id);
        if (data) {
          setQuestionnaire(data);
          setAnswers(data.answers || {});
        } else {
          setError('Не вдалося знайти опитувальник.');
        }
      } catch (err) {
        setError('Помилка при завантаженні даних.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [id, location.search]);

  const handleAnswerChange = (questionName: string, value: any) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionName]: value,
    }));
  };

  const handleSave = async () => {
    if (!id) return;
    console.log('Attempting to save questionnaire...');
    try {
      setLoading(true);

      // Готуємо дані для відправки, коректно конвертуючи числові поля
      const processedAnswers: Record<string, any> = {};
      if (questionnaire && questionnaire.steps) {
        // 1. Збираємо всі питання зі всіх кроків в один масив
        const allQuestions = questionnaire.steps.flatMap(step => step.questions);

        // 2. Визначаємо імена (name) полів, які є числовими
        const numericFieldNames = allQuestions
          .filter(q => q.type === 'number')
          .map(q => q.name); // Використовуємо `name` замість `id`

        for (const key in answers) {
          const value = answers[key];
          // 3. Перевіряємо, чи є ключ (назва поля) у списку числових
          if (numericFieldNames.includes(key) && value !== '' && value !== null) {
            processedAnswers[key] = Number(value);
          } else {
            processedAnswers[key] = value;
          }
        }
      }

      const payload = { answers: Object.keys(processedAnswers).length > 0 ? processedAnswers : answers };
      let response;

      if (isStartSurvey) {
        response = await submitStartSurvey(payload);
      } else {
        response = await submitQuestionnaire(id, payload);
      }

      console.log('Server response:', response);
      if (response.validation.isValid) {
        setSnackbarMessage('Відповіді успішно збережено!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        console.log('Snackbar should open with success message.');
        if (questionnaire) {
          setQuestionnaire({
            ...questionnaire,
            analytics: response.analytics,
          });
        }
        if (isStartSurvey) {
          navigate('/dashboard'); // Повертаємо на головну після стартового опитування
        } else {
          navigate('/questionnaires'); // Для інших опитувань повертаємо до списку
        }
      } else {
        const errorMessages = response.validation.errors.map(err => err.message).join(', ');
        setSnackbarMessage(`Помилки валідації: ${errorMessages}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        console.log('Snackbar should open with validation error message.');
      }
    } catch (err) {
      setSnackbarMessage('Помилка при збереженні відповідей.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error during save:', err);
      console.log('Snackbar should open with general error message.');
    } finally {
      setLoading(false);
      console.log('Loading set to false.');
    }
  };

  const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
    console.log('Snackbar closed.');
  };

  if (loading && !questionnaire) {
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error">{error}</Alert>
      </DashboardLayout>
    );
  }

  if (!questionnaire) {
    return null;
  }

  return (
    <DashboardLayout>
      <Box data-i-doc={PageIndices.QUESTIONNAIRE} sx={{ mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {questionnaire.description}
        </Typography>
        {questionnaire.welcomeNotes && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {questionnaire.welcomeNotes}
          </Alert>
        )}
      </Box>

      <QuestionnaireRenderer
        questionnaire={questionnaire}
        answers={answers}
        onAnswerChange={handleAnswerChange}
      />

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Зберегти відповіді'}
        </Button>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <AlertSnackbar onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </AlertSnackbar>
      </Snackbar>
    </DashboardLayout>
  );
};

export default QuestionnairePage;
